import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast, Toaster } from 'sonner';
import {
  User, Building2, FileText, Upload, CheckCircle, Clock, AlertTriangle,
  CreditCard, Phone, Shield, Heart, ChevronRight, ChevronLeft, Loader2
} from 'lucide-react';

// Use relative URL so it works on any domain (preview, production, etc.)
const API = '';

const STEPS = [
  { id: 'personal', label: 'Personal Details', icon: User },
  { id: 'bank', label: 'Bank & Compliance', icon: CreditCard },
  { id: 'emergency', label: 'Emergency Contact', icon: Phone },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'review', label: 'Review & Submit', icon: CheckCircle },
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const OnboardingPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portal, setPortal] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [uploadingType, setUploadingType] = useState(null);

  const fetchPortal = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/onboarding/portal/${token}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to load onboarding');
      }
      const data = await res.json();
      setPortal(data);
      setForm(data.submitted_data || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPortal(); }, [fetchPortal]);

  const handleSave = async (showToast = true) => {
    try {
      setSaving(true);
      const res = await fetch(`${API}/api/onboarding/portal/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setPortal(prev => ({ ...prev, completion_percentage: data.completion_percentage, status: data.status }));
      if (showToast) toast.success('Progress saved');
    } catch {
      if (showToast) toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/onboarding/portal/${token}/upload?doc_type=${docType}`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      toast.success(data.message);
      setPortal(prev => ({
        ...prev,
        completion_percentage: data.completion_percentage,
        status: data.status,
        documents: [...(prev.documents || []).filter(d => d.doc_type !== docType), data.doc]
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingType(null);
    }
  };

  const nextStep = async () => {
    await handleSave(false);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  const isDocUploaded = (docType) => (portal?.documents || []).some(d => d.doc_type === docType);
  const getDocFilename = (docType) => {
    const doc = (portal?.documents || []).find(d => d.doc_type === docType);
    return doc?.original_filename || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Onboarding Unavailable</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (portal?.status === 'completed' || portal?.status === 'approved') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Onboarding Complete!</h2>
            <p className="text-slate-500">Thank you, {portal.employee_name}. Your onboarding has been submitted successfully. HR will review your information shortly.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const completion = portal?.completion_percentage || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50" data-testid="onboarding-portal">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {portal?.logo_url && (
              <img src={portal.logo_url} alt="Company" className="h-10 w-10 object-contain" onError={e => e.target.style.display='none'} />
            )}
            <div>
              <h1 className="text-lg font-bold text-indigo-600" data-testid="company-name">{portal?.company_name || portal?.company_legal_name || 'Company'}</h1>
              <p className="text-xs text-slate-500">Employee Onboarding Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-700">{portal?.employee_name}</p>
              <p className="text-xs text-slate-400">{portal?.designation} {portal?.department ? `| ${portal.department}` : ''}</p>
            </div>
            <Badge className={completion === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {completion}%
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2" data-testid="step-indicators">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                className={`flex flex-col items-center gap-1.5 min-w-[80px] transition-all ${isActive ? 'scale-105' : ''}`}
                data-testid={`step-${s.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isActive ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : isDone ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {isDone ? <CheckCircle size={18} /> : <Icon size={18} />}
                </div>
                <span className={`text-xs font-medium ${isActive ? 'text-indigo-600' : isDone ? 'text-emerald-600' : 'text-slate-400'}`}>{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Overall Progress</span>
            <span>{completion}% complete</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
          </div>
        </div>

        {/* Step Content */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-slate-50/50">
            <CardTitle className="text-lg flex items-center gap-2">
              {React.createElement(STEPS[step].icon, { size: 20, className: 'text-indigo-500' })}
              {STEPS[step].label}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Step 0: Personal Details */}
            {step === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-personal-form">
                <div>
                  <Label>Gender <span className="text-red-400">*</span></Label>
                  <Select value={form.gender || ''} onValueChange={v => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Birth <span className="text-red-400">*</span></Label>
                  <Input type="date" value={form.date_of_birth || ''} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} data-testid="dob-input" />
                </div>
                <div>
                  <Label>Blood Group <span className="text-red-400">*</span></Label>
                  <Select value={form.blood_group || ''} onValueChange={v => setForm({ ...form, blood_group: v })}>
                    <SelectTrigger data-testid="blood-group-select"><SelectValue placeholder="Select blood group" /></SelectTrigger>
                    <SelectContent>
                      {BLOOD_GROUPS.map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Aadhaar Number <span className="text-red-400">*</span></Label>
                  <Input value={form.aadhaar_number || ''} onChange={e => setForm({ ...form, aadhaar_number: e.target.value })} placeholder="XXXX XXXX XXXX" data-testid="aadhaar-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address <span className="text-red-400">*</span></Label>
                  <Input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address" data-testid="address-input" />
                </div>
                <div>
                  <Label>Medical Insurance</Label>
                  <Select value={form.medical_insurance_status || ''} onValueChange={v => setForm({ ...form, medical_insurance_status: v })}>
                    <SelectTrigger data-testid="medical-select"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="not_applicable">Not Applicable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 1: Bank & Compliance */}
            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-bank-form">
                <div>
                  <Label>Bank Account Number <span className="text-red-400">*</span></Label>
                  <Input value={form.bank_account_number || ''} onChange={e => setForm({ ...form, bank_account_number: e.target.value })} placeholder="Enter account number" data-testid="bank-account-input" />
                </div>
                <div>
                  <Label>IFSC Code <span className="text-red-400">*</span></Label>
                  <Input value={form.ifsc_code || ''} onChange={e => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })} placeholder="e.g. SBIN0001234" data-testid="ifsc-input" />
                </div>
                <div>
                  <Label>Bank Name <span className="text-red-400">*</span></Label>
                  <Input value={form.bank_name || ''} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. State Bank of India" data-testid="bank-name-input" />
                </div>
                <div>
                  <Label>PAN Number <span className="text-red-400">*</span></Label>
                  <Input value={form.pan_number || ''} onChange={e => setForm({ ...form, pan_number: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" data-testid="pan-input" />
                </div>
                <div>
                  <Label>PF Number</Label>
                  <Input value={form.pf_number || ''} onChange={e => setForm({ ...form, pf_number: e.target.value })} placeholder="UAN or PF Number" data-testid="pf-input" />
                </div>
                <div>
                  <Label>ESIC Number</Label>
                  <Input value={form.esic_number || ''} onChange={e => setForm({ ...form, esic_number: e.target.value })} placeholder="ESIC Number" data-testid="esic-input" />
                </div>
              </div>
            )}

            {/* Step 2: Emergency Contact */}
            {step === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-emergency-form">
                <div>
                  <Label>Contact Name <span className="text-red-400">*</span></Label>
                  <Input value={form.emergency_contact_name || ''} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="Full name" data-testid="emergency-name-input" />
                </div>
                <div>
                  <Label>Phone Number <span className="text-red-400">*</span></Label>
                  <Input value={form.emergency_contact_phone || ''} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} placeholder="+91 XXXXXXXXXX" data-testid="emergency-phone-input" />
                </div>
                <div>
                  <Label>Relationship <span className="text-red-400">*</span></Label>
                  <Select value={form.emergency_contact_relation || ''} onValueChange={v => setForm({ ...form, emergency_contact_relation: v })}>
                    <SelectTrigger data-testid="emergency-relation-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spouse">Spouse</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="sibling">Sibling</SelectItem>
                      <SelectItem value="friend">Friend</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: Documents */}
            {step === 3 && (
              <div className="space-y-3" data-testid="step-documents-form">
                <p className="text-sm text-slate-500 mb-4">Upload the following documents (PDF, JPG, PNG - max 10MB each)</p>
                {Object.entries(portal?.document_types || {}).map(([type, label]) => (
                  <div key={type} className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${isDocUploaded(type) ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`doc-row-${type}`}>
                    <div className="flex items-center gap-3">
                      {isDocUploaded(type) ? (
                        <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <FileText size={20} className="text-slate-400 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-700">{label}</p>
                        {isDocUploaded(type) && <p className="text-xs text-emerald-600">{getDocFilename(type)}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isDocUploaded(type) && <Badge variant="outline" className="text-emerald-600 border-emerald-300">Uploaded</Badge>}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          onChange={e => handleUpload(type, e.target.files?.[0])}
                          disabled={uploadingType === type}
                        />
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          uploadingType === type ? 'bg-slate-200 text-slate-400' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        }`}>
                          {uploadingType === type ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          {isDocUploaded(type) ? 'Replace' : 'Upload'}
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-6" data-testid="step-review">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><User size={16} className="text-indigo-500" /> Personal Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Gender</span><span className="font-medium">{form.gender || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Date of Birth</span><span className="font-medium">{form.date_of_birth || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Blood Group</span><span className="font-medium">{form.blood_group || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Aadhaar</span><span className="font-medium">{form.aadhaar_number || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Address</span><span className="font-medium truncate max-w-[200px]">{form.address || <span className="text-red-400">Missing</span>}</span></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><CreditCard size={16} className="text-emerald-500" /> Bank & Compliance</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Bank Account</span><span className="font-medium">{form.bank_account_number ? `XXXX${form.bank_account_number.slice(-4)}` : <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">IFSC</span><span className="font-medium">{form.ifsc_code || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Bank Name</span><span className="font-medium">{form.bank_name || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">PAN</span><span className="font-medium">{form.pan_number || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">PF Number</span><span className="font-medium">{form.pf_number || <span className="text-slate-300">Not provided</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">ESIC</span><span className="font-medium">{form.esic_number || <span className="text-slate-300">Not provided</span>}</span></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Phone size={16} className="text-amber-500" /> Emergency Contact</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="font-medium">{form.emergency_contact_name || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-medium">{form.emergency_contact_phone || <span className="text-red-400">Missing</span>}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Relation</span><span className="font-medium">{form.emergency_contact_relation || <span className="text-red-400">Missing</span>}</span></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><FileText size={16} className="text-blue-500" /> Documents</h3>
                    <div className="space-y-2 text-sm">
                      {Object.entries(portal?.document_types || {}).map(([type, label]) => (
                        <div key={type} className="flex justify-between">
                          <span className="text-slate-500">{label}</span>
                          {isDocUploaded(type) ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Uploaded</Badge>
                          ) : (
                            <span className="text-red-400 text-xs font-medium">Missing</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t">
              <Button variant="outline" onClick={prevStep} disabled={step === 0} data-testid="prev-step-btn">
                <ChevronLeft size={16} className="mr-1" /> Previous
              </Button>
              <Button variant="outline" onClick={() => handleSave(true)} disabled={saving} data-testid="save-progress-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Save Progress
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={nextStep} data-testid="next-step-btn">
                  Next <ChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button onClick={() => handleSave(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-btn">
                  <CheckCircle size={16} className="mr-1" /> Submit Onboarding
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Expiry Notice */}
        <div className="mt-4 text-center">
          <p className="text-xs text-slate-400 flex items-center justify-center gap-1">
            <Clock size={12} />
            This link expires on {new Date(portal?.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPortal;
